import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type GetListingResponse, GetListingResponseStatus } from '@pkgs/api/index';
import { safePanic } from '@pkgs/api';
import { useAuth } from '@ev-tech/user-account-modules';
import { datadogRum } from '@datadog/browser-rum';

import { getListingErrorRedirectTarget } from '@/utils/listingRedirectTargets';
import { setPrivateOfficeCookie } from '@/utils/cookieUtils';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { SearchApiClient } from '@/consts/search-api';

import { getListingQueryKey, LISTING_QUERY_KEY } from './queryKeys';
import { useSettings } from '../useSettings';
import { usePrivateOfficePasswordStore } from '../usePrivateOfficePasswordStore';
import { useExposeLanguageStore } from '../useExposeLanguageStore';

const hasListingQueryData = (data: GetListingResponse | undefined): data is GetListingResponse =>
  Boolean(data?.listing || data?.restrictedListing || data?.userAccountExclusiveListing);

export const useListing = () => {
  const queryClient = useQueryClient();
  const { getToken, userId = '', isLoaded } = useAuth();
  const { listingId, pathLanguage, privateOfficePassword: ssrPrivateOfficePassword, promotedBy } = useExposePageProps();
  const { currency, measurementSystem, countryCode } = useSettings();
  const exposeLanguage = useExposeLanguageStore((state) => state.exposeLanguage ?? pathLanguage);
  const privateOfficePassword = usePrivateOfficePasswordStore((state) => state.privateOfficePassword) ?? ssrPrivateOfficePassword;
  const searchParams = useSearchParams();
  const secretAccessId = searchParams?.get('secretAccessId') ?? undefined;
  const queryString = searchParams?.toString() ?? '';
  const pathname = usePathname();
  const router = useRouter();

  const query = useQuery({
    queryKey: getListingQueryKey({
      listingId,
      language: exposeLanguage,
      currency,
      measurementSystem,
      secretAccessId,
      agentId: promotedBy,
      userId,
    }),
    queryFn: async () => {
      const listingResponse = await SearchApiClient.withConfig({
        privateOfficePassword,
        getToken,
        onError: undefined,
      }).getListingById(listingId, {
        language: exposeLanguage,
        currency,
        marketCountryCode: countryCode,
        agentId: promotedBy,
        measurementSystem,
        secretAccessId,
      });

      if (listingResponse.status === 'error') {
        datadogRum.addError(listingResponse.error, {
          listingId,
          pathLanguage,
          exposeLanguage,
          currency,
          measurementSystem,
          statusCode: listingResponse.statusCode,
          errorMessage: listingResponse.errorMessage,
          source: 'useListing',
        });
        // this redirect should be handled already on the server before the app reaches this code
        // However as recent issues have shown, we might still response the cached version of the expose
        // while the listing was delted already. Following requests of this hook might fail unexpectedly.

        router.replace(
          getListingErrorRedirectTarget({
            listingResponse,
            countryCode,
            language: pathLanguage,
            queryString,
          }),
        );
        throw listingResponse.error;
      }

      if (listingResponse.data.status === GetListingResponseStatus.OK && privateOfficePassword) {
        setPrivateOfficeCookie(privateOfficePassword, pathname ?? '');
      }

      return listingResponse.data;
    },
    refetchOnMount: false,
    enabled: isLoaded,
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      // When there's only query data from getServerSideProps, previousData is undefined.
      // In that case, we return the first entry of the queries data, which will always contain the listing from the server.
      // We only use the fixed start of the query key as a filter here,
      // to avoid slight mismatches between the server and client key causing the query to not find the data.
      const queries = queryClient.getQueriesData<GetListingResponse | undefined>({
        queryKey: [LISTING_QUERY_KEY, listingId],
      });

      const queryWithData = queries.find(([, data]) => hasListingQueryData(data));

      if (!queryWithData)
        return safePanic({
          message: 'No hydrated listing query data from getServerSideProps',
          fallback: {},
          onError: (error) => {
            datadogRum.addError(error, {
              listingId,
              pathLanguage,
              exposeLanguage,
              currency,
              measurementSystem,
              matchingQueryCount: queries.length,
            });
          },
        });

      const [, data] = queryWithData;
      return data;
    },
    select: (data) => {
      if (data.listing) {
        return {
          listing: data.listing,
          genericListing: data.listing,
          status: data.status,
        };
      } else if (data.restrictedListing) {
        return {
          restrictedListing: data.restrictedListing,
          genericListing: data.restrictedListing,
          status: data.status,
        };
      } else if (data.userAccountExclusiveListing) {
        return {
          userAccountExclusiveListing: data.userAccountExclusiveListing,
          genericListing: data.userAccountExclusiveListing,
          status: data.status,
        };
      }
    },
  });
  return {
    ...query,
    data: query.data!,
  };
};
